import React from 'react';
interface HiringFunnelModalProps {
  isOpen: boolean;
  onClose: () => void;
  estimatedHires: number;
}
export function HiringFunnelModal({
  isOpen,
  onClose,
  estimatedHires
}: HiringFunnelModalProps) {
  if (!isOpen) return null;
  // Calculate funnel stages working backwards from hires
  const hires = Math.round(estimatedHires);
  // Final Interview Show-ups (50% show rate, so 2x booked)
  const finalInterviewShowups = hires / 0.5; // Hire 50% of those who show
  const finalInterviewsBooked = finalInterviewShowups * 2; // 50% show rate
  // Initial Interview (50% pass to final, 50% show rate)
  const initialInterviewShowups = finalInterviewShowups / 0.5; // 50% pass rate
  const initialInterviewsBooked = initialInterviewShowups * 2; // 50% show rate
  // Screening Interview (30% pass, 50% show rate)
  const screeningShowups = initialInterviewShowups / 0.3; // 30% pass rate
  const screeningsBooked = screeningShowups * 2; // 50% show rate
  // Contacting (successful contact)
  const contacted = screeningsBooked;
  // Outreach Attempts (2-3x multiplier)
  const outreachAttempts = contacted * 2.5;
  // Applicants (30% contact rate)
  const applicants = contacted / 0.3;
  const stages = [{
    label: 'Applicants',
    value: Math.round(applicants),
    color: 'from-gray-400 to-gray-300',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-300',
    description: 'Total applicants in pipeline'
  }, {
    label: 'Outreach Attempts',
    value: Math.round(outreachAttempts),
    multiplier: '×2.5',
    color: 'from-gray-500 to-gray-400',
    bgColor: 'bg-gray-100',
    borderColor: 'border-gray-400',
    description: 'Multiple contact attempts per applicant',
    conversionNote: '30% contact rate'
  }, {
    label: 'Successfully Contacted',
    value: Math.round(contacted),
    color: 'from-blue-400 to-blue-300',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
    description: 'Applicants reached and engaged'
  }, {
    label: 'Screening Interviews Booked',
    value: Math.round(screeningsBooked),
    color: 'from-blue-500 to-blue-400',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-400',
    description: 'Scheduled screening calls',
    conversionNote: '50% show rate'
  }, {
    label: 'Screening Interviews Completed',
    value: Math.round(screeningShowups),
    color: 'from-indigo-400 to-indigo-300',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-300',
    description: 'Candidates who showed up',
    conversionNote: '30% pass rate'
  }, {
    label: 'Initial Interviews Booked',
    value: Math.round(initialInterviewsBooked),
    color: 'from-indigo-500 to-indigo-400',
    bgColor: 'bg-indigo-100',
    borderColor: 'border-indigo-400',
    description: 'First round interviews scheduled',
    conversionNote: '50% show rate'
  }, {
    label: 'Initial Interviews Completed',
    value: Math.round(initialInterviewShowups),
    color: 'from-purple-400 to-purple-300',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-300',
    description: 'Candidates who attended',
    conversionNote: '50% pass rate'
  }, {
    label: 'Final Interviews Booked',
    value: Math.round(finalInterviewsBooked),
    color: 'from-purple-500 to-purple-400',
    bgColor: 'bg-purple-100',
    borderColor: 'border-purple-400',
    description: 'Final round scheduled',
    conversionNote: '50% show rate'
  }, {
    label: 'Final Interviews Completed',
    value: Math.round(finalInterviewShowups),
    color: 'from-amber-400 to-amber-300',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
    description: 'Candidates who showed up',
    conversionNote: '50% offer rate'
  }, {
    label: 'Offers Extended',
    value: Math.round(finalInterviewShowups * 0.5),
    color: 'from-amber-500 to-amber-400',
    bgColor: 'bg-amber-100',
    borderColor: 'border-amber-400',
    description: 'Job offers made',
    conversionNote: '~100% acceptance'
  }, {
    label: 'Hires',
    value: hires,
    color: 'from-green-500 to-green-400',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-500',
    description: 'Successfully hired candidates',
    highlight: true
  }];
  const maxValue = Math.round(applicants);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-578" data-mp="6gwvu5ik3w1">
      <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto border-4 border-navy" data-id="element-579" data-mp="3rdl1kie6re1a">
        {/* Header */}
        <div className="sticky top-0 bg-navy text-white p-6 flex items-center justify-between border-b-4 border-amber z-10" data-id="element-580" data-mp="nv9udv1sajsr">
          <div data-id="element-581" data-mp="ivf3vslx7w88">
            <h2 className="text-2xl md:text-3xl font-bold mb-2" data-id="element-582" data-mp="iuvlkascz5to">
              Complete Hiring Funnel Breakdown
            </h2>
            <p className="text-blue-200 text-base" data-id="element-583" data-mp="ewo5zj9msak2">
              See the full pipeline required to achieve {hires} monthly hires
            </p>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors flex-shrink-0 ml-4" aria-label="Close" data-id="element-584" data-mp="2zjrajow272qu">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-585" data-mp="1j3lnr2secnfh">
              <line x1="18" y1="6" x2="6" y2="18" data-id="element-586" data-mp="1o1n1d871zujk" />
              <line x1="6" y1="6" x2="18" y2="18" data-id="element-587" data-mp="1sv81p6w4d0lx" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8" data-id="element-588" data-mp="1xszx4avz34uv">
          <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded" data-id="element-589" data-mp="22s436snlomgz">
            <p className="text-base text-gray-700 leading-relaxed" data-id="element-590" data-mp="22u74i8u7e210">
              <span className="font-bold text-navy" data-id="element-591" data-mp="26uh7rdqm2ec6">The Reality:</span> To hire{' '}
              <span className="font-bold text-green-600" data-id="element-592" data-mp="2brr6xl3sy4d7">{hires} people</span>{' '}
              per month, you need to start with approximately{' '}
              <span className="font-bold text-gray-700" data-id="element-593" data-mp="hf1d13dfov5t">
                {Math.round(applicants)} applicants
              </span>{' '}
              and make{' '}
              <span className="font-bold text-gray-700" data-id="element-594" data-mp="1kqgrkj4bubiz">
                {Math.round(outreachAttempts)} outreach attempts
              </span>
              . This visualization shows the typical conversion rates at each
              stage of the hiring process.
            </p>
          </div>

          {/* Funnel Visualization */}
          <div className="space-y-4" data-id="element-595" data-mp="23ot3udqjc1is">
            {stages.map((stage, index) => {
            const widthPercent = stage.value / maxValue * 100;
            const isMultiplier = stage.multiplier !== undefined;
            return <div key={stage.label} data-id="element-596" data-mp="2chmm8k1a3h0v" data-mpi={index}>
                  {/* Stage Card - Retention Funnel Style */}
                  <div className="flex items-center gap-3" data-id="element-597" data-mp="28ezraa9b9fzg">
                    <div className="w-24 text-right" data-id="element-598" data-mp="28hm6mw2g0zs8">
                      <span className={`text-xl font-bold ${stage.highlight ? 'text-green-600' : 'text-navy'}`} data-id="element-599" data-mp="2mgc6ukkk7fkt">
                        {stage.value.toLocaleString()}
                      </span>
                    </div>
                    <div className="w-48 text-sm font-semibold text-gray-700" data-id="element-600" data-mp="312ix8p71vk1v">
                      {stage.label}
                      {isMultiplier && <span className="ml-2 px-2 py-0.5 bg-white border border-gray-400 rounded text-xs font-bold text-gray-700" data-id="element-601" data-mp="35yq3yk79szkv">
                          {stage.multiplier}
                        </span>}
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-602" data-mp="2ib89ggwmfne7">
                      <div className={`absolute inset-y-0 left-0 bg-gradient-to-r ${stage.color} rounded-full`} style={{
                    width: `${widthPercent}%`
                  }} data-id="element-603" data-mp="2n2gjnf5rzcsf" />
                    </div>
                    {stage.conversionNote && <div className="w-24 text-sm font-semibold text-gray-600 text-right" data-id="element-604" data-mp="2dcol69uk1zio">
                        {stage.conversionNote}
                      </div>}
                  </div>

                  {/* Arrow */}
                  {index < stages.length - 1 && <div className="flex justify-center py-2" data-id="element-605" data-mp="2ju9wl6mzzafi">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1e3a8a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-606" data-mp="2jre07mqsstt6">
                        <line x1="12" y1="5" x2="12" y2="19" data-id="element-607" data-mp="29u99d9m0ca61" />
                        <polyline points="19 12 12 19 5 12" data-id="element-608" data-mp="24wpty6dkprci" />
                      </svg>
                    </div>}
                </div>;
          })}
          </div>

          {/* Summary Stats */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-609" data-mp="1w4fmgwu2bzyy">
            <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-4 text-center" data-id="element-610" data-mp="1w22v5t2f0d98">
              <div className="text-sm text-gray-600 font-semibold mb-1" data-id="element-611" data-mp="1r2ylz2fhqfiw">
                Total Pipeline
              </div>
              <div className="text-3xl font-bold text-navy" data-id="element-612" data-mp="1h6vjfvgq8evf">
                {Math.round(applicants)}
              </div>
              <div className="text-xs text-gray-500 mt-1" data-id="element-613" data-mp="1d4rzeifzs1yj">
                Applicants needed
              </div>
            </div>
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 text-center" data-id="element-614" data-mp="3os3s7ed6znxu">
              <div className="text-sm text-gray-600 font-semibold mb-1" data-id="element-615" data-mp="3ssxe6272qso3">
                Conversion Rate
              </div>
              <div className="text-3xl font-bold text-amber-600" data-id="element-616" data-mp="1luojf6vwufz">
                {(hires / applicants * 100).toFixed(1)}%
              </div>
              <div className="text-xs text-gray-500 mt-1" data-id="element-617" data-mp="35562n0tcyjnv">
                Applicant to hire
              </div>
            </div>
            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 text-center" data-id="element-618" data-mp="3a4in5ewrbena">
              <div className="text-sm text-gray-600 font-semibold mb-1" data-id="element-619" data-mp="3f224inor47jg">
                Final Output
              </div>
              <div className="text-3xl font-bold text-green-600" data-id="element-620" data-mp="3juvb7h3s5py6">{hires}</div>
              <div className="text-xs text-gray-500 mt-1" data-id="element-621" data-mp="2mddblbxun8is">Hires per month</div>
            </div>
          </div>

          {/* Close Button */}
          <div className="mt-8 flex justify-center" data-id="element-622" data-mp="3q43asrerkplz">
            <button onClick={onClose} className="px-6 py-3 bg-navy hover:bg-navy/90 text-white font-bold rounded-lg transition-colors shadow-lg" data-id="element-623" data-mp="3q3a0jggljhz3">
              Close Funnel View
            </button>
          </div>
        </div>
      </div>
    </div>;
}